import { Loading, TextInput } from "@vellum/ui";
import type { InternalCompanySchema } from "@vellum/vellum-api";
import { useEffect, useMemo, useState, type ChangeEvent } from "react";

import { vellumApiSdk } from "@vellum/micro-app-auth";
import { ChevronRightIcon } from "@/components/ChevronRightIcon";

/** Searchable list of all companies. Filters locally by name. */
export function CompanySelect({
  onSelect,
}: {
  onSelect: (company: InternalCompanySchema) => void;
}) {
  const [companies, setCompanies] = useState<InternalCompanySchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let active = true;
    vellumApiSdk
      .listCompanies()
      .then((res) => {
        if (active) setCompanies(res.data ?? []);
      })
      .catch(() => {
        if (active) setError("Something went wrong loading companies.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? companies.filter((c) => c.name.toLowerCase().includes(q))
      : companies;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [companies, query]);

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-3 p-4">
      <TextInput
        id="company-search"
        placeholder="Search companies"
        value={query}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setQuery(e.target.value)
        }
      />
      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loading />
        </div>
      ) : error ? (
        <p className="py-10 text-center text-sm text-error">{error}</p>
      ) : filtered.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">
          No companies found.
        </p>
      ) : (
        <ul className="flex flex-1 flex-col divide-y divide-gray-200 overflow-y-auto rounded-md border border-gray-200 bg-white">
          {filtered.map((company) => (
            <li key={company.id}>
              <button
                type="button"
                onClick={() => onSelect(company)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-100"
              >
                <span className="text-sm font-medium text-gray-900">
                  {company.name}
                </span>
                <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
